import { useEffect, useState } from 'react'
import { getGrowthTrace, type GrowthCounted, type GrowthTraceStep } from '../lib/adminClient'
import { num, pct } from '../lib/format'
import { DrillValue } from './Drill'
import { LoadError } from './LoadError'
import { DetailGrid, ExpandableRow, useOpenRow } from './RowDetail'

// Where people stop between joining and paying (GET /admin/growth/trace,
// server: adminGrowth.ts). Unlike the event counts below it on the Funnel
// page, this follows one group of people: everyone who joined in the range,
// step by step. Each step splits the people from the step before into reached,
// stopped, can't tell (the records cannot say) and too early to tell. Every
// number opens those accounts.

type State = { s: 'loading' } | { s: 'ready'; steps: GrowthTraceStep[] } | { s: 'error'; error: string }

function Count({ c, label, testId }: { c: GrowthCounted; label: string; testId: string }): JSX.Element {
  if (c.count === 0) return <span className="text-slate-500 tabular-nums">0</span>
  return (
    <DrillValue to={c.ref} label={label} expect={{ key: 'count', value: c.count }} testId={testId}>
      <span className="tabular-nums">{num(c.count)}</span>
    </DrillValue>
  )
}

export function FunnelTrace({ from, to }: { from: string; to: string }): JSX.Element {
  const [state, setState] = useState<State>({ s: 'loading' })
  const open = useOpenRow()

  useEffect(() => {
    let live = true
    setState({ s: 'loading' })
    void getGrowthTrace(from, to).then((res) => {
      if (!live) return
      setState(res.ok ? { s: 'ready', steps: res.data.steps } : { s: 'error', error: res.error })
    })
    return () => {
      live = false
    }
  }, [from, to])

  const range = `joined ${from} to ${to}`
  return (
    <div className="mb-8" data-testid="trace">
      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">Where people stop</div>
      <div className="text-sm text-slate-400 mb-3">
        Everyone who joined {from} to {to}, followed step by step. Open a step to see what the records say about the people who stopped there.
      </div>
      {state.s === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading…</div>}
      {state.s === 'error' && <LoadError what="the funnel trace" endpoint="GET /admin/growth/trace" error={state.error} testId="trace-error" />}
      {state.s === 'ready' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table" data-testid="trace-table">
            <thead>
              <tr>
                <th>Step</th>
                <th className="text-right">Of</th>
                <th className="text-right">Reached</th>
                <th className="text-right">Stopped</th>
                <th className="text-right" title="The records cannot say either way">Can’t tell</th>
                <th className="text-right" title="Joined less than a day ago">Too early</th>
                <th className="text-right">Kept</th>
              </tr>
            </thead>
            <tbody>
              {state.steps.map((s) => {
                const decided = s.reached.count + s.dropped.count
                return (
                  <ExpandableRow
                    key={s.key}
                    open={open.isOpen(s.key)}
                    onToggle={() => open.toggle(s.key)}
                    colSpan={7}
                    testId={`trace-row-${s.key}`}
                    detail={() => (
                      <DetailGrid
                        facts={[
                          ['People at the step before', num(s.from)],
                          ['Reached it', num(s.reached.count)],
                          ['Stopped before it', num(s.dropped.count)],
                          ['Can’t tell', s.unknown.count ? `${num(s.unknown.count)}, left out of “Kept”` : 'None'],
                          ['Too early to tell', s.waiting.count ? `${num(s.waiting.count)}, left out of “Kept”` : 'None']
                        ]}
                        wide={[['What it means', s.means]]}
                      >
                        {s.why.length > 0 && (
                          <div className="mt-3" data-testid={`trace-why-${s.key}`}>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                              What the records say about the {num(s.dropped.count)} who stopped (one person can match more than one line)
                            </div>
                            <ul className="space-y-1 text-sm max-w-xl">
                              {s.why.map((w) => (
                                <li key={w.key} className="flex justify-between gap-6">
                                  <span className="text-slate-300">{w.label}</span>
                                  <Count c={w} label={`Funnel (${range}): stopped before “${s.label}”, ${w.label.toLowerCase()}`} testId={`trace-why-${s.key}-${w.key}`} />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </DetailGrid>
                    )}
                  >
                    <td className="font-medium text-slate-100">{s.label}</td>
                    <td className="text-right tabular-nums text-slate-400">{s.key === 'joined' ? '' : num(s.from)}</td>
                    <td className="text-right text-base" data-testid={`trace-reached-${s.key}`}>
                      <Count c={s.reached} label={`Funnel (${range}): reached “${s.label}”`} testId={`trace-drill-reached-${s.key}`} />
                    </td>
                    <td className="text-right" data-testid={`trace-dropped-${s.key}`}>
                      {s.key === 'joined' ? '' : <Count c={s.dropped} label={`Funnel (${range}): stopped before “${s.label}”`} testId={`trace-drill-dropped-${s.key}`} />}
                    </td>
                    <td className="text-right">{s.key === 'joined' ? '' : <Count c={s.unknown} label={`Funnel (${range}): can’t tell about “${s.label}”`} testId={`trace-drill-unknown-${s.key}`} />}</td>
                    <td className="text-right">{s.key === 'joined' ? '' : <Count c={s.waiting} label={`Funnel (${range}): too early for “${s.label}”`} testId={`trace-drill-waiting-${s.key}`} />}</td>
                    <td className="text-right tabular-nums" data-testid={`trace-kept-${s.key}`}>
                      {s.key === 'joined' ? '' : decided ? pct(s.reached.count, decided) : '—'}
                    </td>
                  </ExpandableRow>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs text-slate-500 mt-2">
        “Kept” is reached out of reached plus stopped. People the records cannot speak for (usage reporting off and still signed in from day one) and people who
        joined less than a day ago are counted separately, not as lost.
      </div>
    </div>
  )
}
